import Vue from "vue";
import { getDefaultState } from "./state";

/**
 *  Mutations for the assignment, submissions and outcomes store
 *
 * @module Assignments
 * @category Store
 * @subcategory Assignments
 * @name Mutations
 */

export default {
  /**
   * Reset state to default empty state
   *
   * @param { object } state State
   */
  resetStateData: state => {
    Object.assign(state, getDefaultState());
  },

  /**
   * Insert/replace assignments in state
   *
   * @param { object } state State
   * @param { object } payload Payload
   * @param { string }payload.sectionId Id of section to insert
   * @param { Array<object> } payload.assignments Assignments on section
   */
  loadForSection: (state, { sectionId, assignments }) => {
    // Map assignments to section
    const assignmentIdsForSection = assignments.map(a => a.id);
    Vue.set(state.assignmentsForSectionMap, sectionId, assignmentIdsForSection);

    const stateClone = Object.assign({}, state.assignments);

    assignments.map(a => {
      stateClone[a.id] = a;
    });

    Vue.set(state, "assignments", stateClone);
  },

  reload: (state, assignment) => {
    Vue.set(state.assignments, assignment.id, assignment);
  },

  /**
   * Create/replace assignment in state
   *
   * @param { object } state State
   * @param { object } payload Payload
   * @param { string } payload.sectionId Id of section to insert
   * @param { object } payload.assignment Assignment to create/replace
   */
  persistAssignmentForSection: (state, { sectionId, assignment }) => {
    state.assignments[assignment.id] = assignment;

    const clone = Object.assign({}, state.assignmentsForSectionMap);

    if (!clone[sectionId]) {
      clone[sectionId] = [];
    }

    if (!clone[sectionId].find(a => a === assignment.id)) {
      clone[sectionId].push(assignment.id);
      Vue.set(state, "assignmentsForSectionMap", clone);
    }
  },

  /**
   * Delete assignment from state
   *
   * @param { object } state State
   * @param { string } assignmentId Id of assignment to delete
   */
  deleteAssignment: (state, assignmentId) => {
    delete state.assignments[assignmentId];
  },

  /**
   * Set categories for assignments in state (from a batch)
   *
   * @param { object } state State
   * @param { object } batchPayload List of categories for assignments
   */
  loadCategoriesForAssignments: (state, batchPayload) => {
    for (let i = 0; i < batchPayload.length; i++) {
      if (batchPayload[i].status === 200) {
        Vue.set(state.categoriesForAssignments, batchPayload[i].id, batchPayload[i].body.value);
      }
    }
  },

  /**
   * Set submissions for assignment in state
   *
   * @param { object } state State
   * @param { object } payload Payload
   * @param { string } payload.assignmentId Id of assignment to set submissions for
   * @param { Array<object> } payload.submissions Array of submissions
   */
  loadSubmissions: (state, { assignmentId, submissions }) => {
    const clone = Object.assign({}, state.submissionsForAssignmentMap);
    clone[assignmentId] = submissions;

    Vue.set(state, "submissionsForAssignmentMap", clone);
  },

  /**
   * Set specific submission for assignment in state
   *
   * @param { object } state State
   * @param { object } payload Payload
   * @param { string } payload.assignmentId Id of assignment to set submission for
   * @param { object } payload.submission Submission
   */
  loadSubmission: (state, { assignmentId, submission }) => {
    const submissions = state.submissionsForAssignmentMap[assignmentId];

    if (submissions && submissions.length > 0) {
      const index = submissions.findIndex(s => s.id === submission.id);
      if (index > -1) {
        submissions[index] = submission;
      } else {
        submissions.push(submission);
      }

      Vue.set(state.submissionsForAssignmentMap, assignmentId, []);
      Vue.set(state.submissionsForAssignmentMap, assignmentId, submissions);
    }
  },

  createSubmissionResourcesFolder: (state, { assignmentId, submissionId, resourcesFolderUrl }) => {
    const submissions = state.submissionsForAssignmentMap[assignmentId];

    if (submissions && submissions.length > 0) {
      const index = submissions.findIndex(s => s.id === submissionId);
      if (index > -1) {
        submissions[index].resourcesFolderUrl = resourcesFolderUrl;
      }

      Vue.set(state.submissionsForAssignmentMap, assignmentId, []);
      Vue.set(state.submissionsForAssignmentMap, assignmentId, submissions);
    }
  },

  loadResources: (state, { assignmentId, resources }) => {
    if (state.assignments[assignmentId]) {
      Vue.set(state.assignments[assignmentId], "resources", resources);
    }
  },

  setFilterAndSortStatusForAssignments: (state, status) => {
    Vue.set(state, "filterAndSortStatusForAssignments", status);
  },

  loadGradeTypes: (state, gradeTypes) => {
    state.gradeTypes = gradeTypes;
  },

  setGradetypeOnAssignment: (state, payload) => {
    state.assignmentGradeTypeMap[payload.assignmentId] = payload.gradeType;
  },

  getGradeTypeOnAssignment: (state, payload) => {
    state.assignmentGradeTypeMap[payload.assignmentId] = payload.gradeType;
  }
};
